import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { enqueue } from '../lib/syncQueue';
import { toast } from 'sonner';

const FoodlistContext = createContext(null);
const API_URL = process.env.REACT_APP_BACKEND_URL;

const offlineFetch = async (path, method, body) => {
  const token = localStorage.getItem('fq_auth_token');
  const opts = {
    method,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  };
  if (body) opts.body = JSON.stringify(body);

  try {
    const res = await fetch(`${API_URL}${path}`, opts);
    return res;
  } catch {
    // Network error — queue for later
    await enqueue({ path, method, body, label: `Foodlist ${method}` });
    toast.info('Saved offline — will sync when connected', { duration: 2000, position: 'bottom-center' });
    return null;
  }
};

export const FoodlistProvider = ({ children }) => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchFoodlist = useCallback(async () => {
    if (!user) { setItems([]); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/foodlist`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setItems(data.items || []);
      }
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [user]);

  useEffect(() => { fetchFoodlist(); }, [fetchFoodlist]);

  const isInFoodlist = (name, restaurant) =>
    items.some((i) => i.name === name && i.restaurant === restaurant);

  const toggleFoodlist = async (dish) => {
    if (!user) return false;
    const inList = isInFoodlist(dish.name, dish.restaurant);

    // Optimistic update first
    if (inList) {
      setItems((prev) => prev.filter((i) => !(i.name === dish.name && i.restaurant === dish.restaurant)));
      const res = await offlineFetch('/api/foodlist/remove', 'POST', { name: dish.name, restaurant: dish.restaurant });
      if (res && !res.ok) {
        // Revert on server error
        setItems((prev) => [dish, ...prev]);
      }
    } else {
      const tempItem = { ...dish, item_id: `temp_${Date.now()}` };
      setItems((prev) => [tempItem, ...prev]);
      const res = await offlineFetch('/api/foodlist', 'POST', dish);
      if (res && res.ok) {
        const data = await res.json();
        setItems((prev) => prev.map(i => i.item_id === tempItem.item_id ? data.item : i));
      }
      // If res is null (queued offline), keep the temp item
    }
    return true;
  };

  const removeById = async (name, restaurant) => {
    setItems((prev) => prev.filter((i) => !(i.name === name && i.restaurant === restaurant)));
    await offlineFetch('/api/foodlist/remove', 'POST', { name, restaurant });
  };

  const removeFromFoodlist = async (itemId) => {
    const item = items.find(i => i.item_id === itemId);
    if (item) {
      await removeById(item.name, item.restaurant);
    }
  };

  // Listen for sync complete from SW
  useEffect(() => {
    const handler = (event) => {
      if (event.data?.type === 'SYNC_COMPLETE') {
        fetchFoodlist(); // Refresh after sync
      }
    };
    navigator.serviceWorker?.addEventListener('message', handler);
    return () => navigator.serviceWorker?.removeEventListener('message', handler);
  }, [fetchFoodlist]);

  return (
    <FoodlistContext.Provider value={{ 
      items, 
      loading,
      isInFoodlist, 
      toggleFoodlist, 
      removeById, 
      removeFromFoodlist,
      refreshFoodlist: fetchFoodlist,
      refetch: fetchFoodlist 
    }}>
      {children}
    </FoodlistContext.Provider>
  );
};

export const useFoodlist = () => {
  const ctx = useContext(FoodlistContext);
  if (!ctx) throw new Error('useFoodlist must be used within FoodlistProvider');
  return ctx;
};
